import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ImpersonateRequest,
  ImpersonateResponse,
  LoginRequest,
  LoginResponse,
  SwitchClientResponse,
} from '@core/models/auth.model';
import { Observable } from 'rxjs';
import { EnvironmentService } from '../environment/environment.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly environment = inject(EnvironmentService);
  private readonly http = inject(HttpClient);

  private readonly API_URL = this.environment.getApiUrl();

  public login(credentials: LoginRequest, consentId?: number | null): Observable<LoginResponse> {
    let headers = new HttpHeaders({
      'x-client-app': 'internal-area',
    });

    if (consentId) {
      headers = headers.set('x-consent-id', String(consentId));
    }

    return this.http.post<LoginResponse>(`${this.API_URL}v1/auth/login`, credentials, { headers });
  }

  public forgotPassword(email: string): Observable<any> {
    return this.http.post<any>(`${this.API_URL}v1/users/forgot-password/${email}`, {});
  }

  public verifyToken(token: string): Observable<any> {
    return this.http.get<any>(`${this.API_URL}v1/users/verify-token/`, {
      params: { token },
    });
  }

  public resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${this.API_URL}v1/users/reset-password/?token=${token}`, {
      new_password: newPassword,
    });
  }

  public impersonate(payload: ImpersonateRequest): Observable<ImpersonateResponse> {
    const headers = new HttpHeaders({ 'x-client-app': 'internal-area' });
    return this.http.post<ImpersonateResponse>(`${this.API_URL}v1/auth/impersonate`, payload, {
      headers,
    });
  }

  public endImpersonation(): Observable<void> {
    return this.http.post<void>(`${this.API_URL}v1/auth/impersonate/end`, {});
  }

  public switchClient(clientId: number): Observable<SwitchClientResponse> {
    const headers = new HttpHeaders({ 'x-client-app': 'internal-area' });
    return this.http.post<SwitchClientResponse>(
      `${this.API_URL}v1/auth/switch-client`,
      { client_id: clientId },
      { headers },
    );
  }

  /**
   * Genera la URL para iniciar el flujo OAuth con Google.
   * Incluye origin=admin para redirigir correctamente al frontend-admin.
   * @param clientId - ID del cliente opcional (para asociación)
   * @returns URL del endpoint OAuth de Google
   */
  public getGoogleLoginUrl(clientId?: number | null): string {
    let url = `${this.API_URL}v1/end_user/oauth/google/login?origin=admin`;

    if (typeof clientId === 'number' && Number.isFinite(clientId)) {
      url += `&client_id=${clientId}`;
    }

    return url;
  }

  /**
   * Genera la URL para iniciar el flujo OAuth con LinkedIn.
   * Incluye origin=admin para redirigir correctamente al frontend-admin.
   * @param clientId - ID del cliente opcional (para asociación)
   * @returns URL del endpoint OAuth de LinkedIn
   */
  public getLinkedInLoginUrl(clientId?: number | null): string {
    let url = `${this.API_URL}v1/end_user/oauth/linkedin/login?origin=admin`;

    if (typeof clientId === 'number' && Number.isFinite(clientId)) {
      url += `&client_id=${clientId}`;
    }

    return url;
  }
}
